import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2, ShieldAlert, Send, Copy, Eye, EyeOff, Upload, FileCheck2,
  CheckCircle2, XCircle, AlertTriangle, Clock, Download,
} from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const BG_BADGE = {
  not_started: { label: "—", cls: "bg-stone-50 text-stone-500 border-stone-200" },
  pending_consent: { label: "Pending consent", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ready_to_check: { label: "Ready to check", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  in_progress: { label: "In progress", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  clear: { label: "Clear", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  consider: { label: "Consider", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  fail: { label: "Fail", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

export function bgCheckBadge(status) {
  return BG_BADGE[status] || BG_BADGE.not_started;
}

async function openAuthedFile(endpoint) {
  const r = await fetch(`${API_BASE}${endpoint}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error("fetch failed");
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export function BackgroundCheckSection({ appId, appStatus, onChanged, locale }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [bg, setBg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalReviewed, setLegalReviewed] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/cleaner-applications/${appId}/bg-check`);
      setBg(unwrap(r));
    } catch { toast.error("Could not load background check status"); }
    finally { setLoading(false); }
  }, [appId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setLegalReviewed(!!unwrap(r)?.value?.bg_check_legal_reviewed)).catch(() => {});
  }, []);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Action failed");
    } finally { setBusy(false); }
  };

  if (loading || !bg) return <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#9CA3AF]" /></div>;

  const hideEntirely = appStatus === "approved" && bg.bg_check_status === "not_started";
  if (hideEntirely) return null;

  return (
    <div className="rounded-xl border border-[#E5E7EB] p-4 space-y-4" data-testid="bg-check-section">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold flex items-center gap-2 text-[#4B5563]">
          <ShieldAlert className="w-4 h-4 text-[#0EA5A4]" /> Background Check
        </div>
        <Badge className={`border font-medium ${bgCheckBadge(bg.bg_check_status).cls}`} data-testid="bg-check-status-badge">
          {bgCheckBadge(bg.bg_check_status).label}
        </Badge>
      </div>

      {!legalReviewed && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700 font-medium" data-testid="bg-check-legal-review-banner">
          ⚠ Legal review pending — do not use with real candidates until sign-off from counsel
        </div>
      )}

      {bg.bg_check_status === "not_started" && (
        <Button onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/send-offer`), "Conditional offer sent")}
          disabled={busy} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="send-conditional-offer-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Send conditional offer + request background check
        </Button>
      )}

      {appStatus === "conditional_offer_sent" && (
        <div className="space-y-2 text-sm" data-testid="bg-check-offer-sent-block">
          <div>Status: <b>Conditional offer sent — awaiting consent</b></div>
          <div className="text-xs text-[#9CA3AF]">Expires: {formatDateTime(bg.consent_token_expires_at, locale)}</div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-lg text-xs"
              onClick={() => { navigator.clipboard.writeText(bg.consent_url); toast.success("Link copied"); }} data-testid="copy-consent-link-btn">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
            </Button>
            <Button size="sm" variant="outline" className="rounded-lg text-xs" disabled={busy}
              onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/resend-offer`), "Email resent")} data-testid="resend-offer-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Resend email
            </Button>
          </div>
        </div>
      )}

      {["background_check_pending", "background_check_completed", "adverse_action_dispute_window", "rejected_after_bg_check"].includes(appStatus) && (
        <ConsentSummary bg={bg} appId={appId} isOwner={isOwner} locale={locale} />
      )}

      {appStatus === "background_check_pending" && (
        <ReportUploadForm appId={appId} isOwner={isOwner} busy={busy} run={run} />
      )}

      {appStatus === "background_check_completed" && (
        <ResultActions bg={bg} appId={appId} isOwner={isOwner} busy={busy} run={run} />
      )}

      {appStatus === "adverse_action_dispute_window" && (
        <DisputeWindow bg={bg} appId={appId} isOwner={isOwner} busy={busy} run={run} />
      )}

      {appStatus === "rejected_after_bg_check" && (
        <div className="text-sm text-[#4B5563]" data-testid="bg-check-final-decision-sent">
          Final decision sent on {formatDateTime(bg.adverse_action?.final_adverse_sent_at, locale)}.
        </div>
      )}

      {appStatus === "approved" && bg.bg_check_status !== "not_started" && (
        <div className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="bg-check-activated-summary">
          <CheckCircle2 className="w-4 h-4" /> Activated as cleaner — background check {bg.result || "clear"}.
        </div>
      )}
    </div>
  );
}

function ConsentSummary({ bg, appId, isOwner, locale }) {
  const [reveal, setReveal] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);

  const doReveal = async () => {
    if (reveal) { setReveal(null); return; }
    setRevealing(true);
    try {
      const r = await api.post(`/admin/cleaner-applications/${appId}/bg-check/reveal-sensitive`);
      setReveal(unwrap(r));
    } catch (err) { toast.error(err?.response?.data?.detail?.message || "Could not reveal"); }
    finally { setRevealing(false); }
  };

  return (
    <div className="space-y-2 text-sm" data-testid="bg-check-consent-summary">
      <div>Status: <b>Consent received</b> — {formatDateTime(bg.consent_completed_at, locale)}</div>
      <div className="flex items-center gap-4 flex-wrap">
        <span>DOB: {reveal ? <b data-testid="bg-check-dob-value">{reveal.date_of_birth}</b> : "••••••••"}</span>
        <span>SSN last 4: {reveal ? <b data-testid="bg-check-ssn-value">{reveal.ssn_last_four}</b> : "••••"}</span>
        {isOwner ? (
          <Button size="sm" variant="outline" className="rounded-lg text-xs h-7" onClick={doReveal} disabled={revealing} data-testid="reveal-sensitive-btn">
            {revealing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : reveal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </Button>
        ) : (
          <span className="text-xs text-[#9CA3AF]" data-testid="reveal-sensitive-owner-note">Owner access required</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button type="button" className="text-xs text-[#0F766E] font-semibold underline" onClick={() => setShowAddresses((s) => !s)} data-testid="toggle-address-history-btn">
          {showAddresses ? "Hide" : "Show"} address history ({(bg.address_history || []).length})
        </button>
        {bg.has_signature && isOwner && (
          <button type="button" className="text-xs text-[#0F766E] font-semibold underline"
            onClick={() => openAuthedFile(`/admin/cleaner-applications/${appId}/bg-check/signature`).catch(() => toast.error("Could not load signature"))}
            data-testid="view-signature-btn">
            View signature
          </button>
        )}
      </div>
      {showAddresses && (
        <ul className="text-xs text-[#4B5563] space-y-1 pl-3 border-l border-[#E5E7EB]" data-testid="address-history-list">
          {(bg.address_history || []).map((a, i) => (
            <li key={i}>{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.zip_code} ({a.from_date} → {a.to_date})</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReportUploadForm({ appId, isOwner, busy, run }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState("clear");
  const [notes, setNotes] = useState("");

  if (!isOwner) {
    return <p className="text-xs text-[#9CA3AF]" data-testid="report-upload-owner-note">Only an account owner can upload the background check result.</p>;
  }

  const submit = () => {
    if (!file) { toast.error("Please select the report PDF"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("result", result);
    fd.append("notes", notes);
    run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/report`, fd), "Result saved");
  };

  return (
    <div className="rounded-lg bg-[#FAFAF9] border border-[#E5E7EB] p-3 space-y-3" data-testid="report-upload-form">
      <p className="text-xs text-[#4B5563]">Now run the check with your provider (iCORI, Checkr, etc) and upload the result below:</p>
      <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="text-xs" data-testid="report-file-input" />
      <RadioGroup value={result} onValueChange={setResult} className="flex gap-4" data-testid="report-result-radio">
        <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="clear" data-testid="report-result-clear" /> Clear</label>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="consider" data-testid="report-result-consider" /> Consider</label>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer"><RadioGroupItem value="fail" data-testid="report-result-fail" /> Fail</label>
      </RadioGroup>
      <Textarea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-lg text-sm" data-testid="report-notes-input" />
      <Button size="sm" onClick={submit} disabled={busy} className="rounded-lg bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="save-report-result-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Save result
      </Button>
    </div>
  );
}

function ResultActions({ bg, appId, isOwner, busy, run }) {
  const badge = bgCheckBadge(bg.result || "clear");
  return (
    <div className="space-y-3" data-testid="bg-check-result-actions">
      <div className="flex items-center gap-2 text-sm">
        <FileCheck2 className="w-4 h-4 text-[#4B5563]" />
        Uploaded by <b>{bg.admin_email || "—"}</b> on {formatDateTime(bg.result_uploaded_at)}
        {bg.has_report && (
          <button type="button" className="text-[#0F766E] font-semibold underline text-xs ml-2"
            onClick={() => openAuthedFile(`/admin/cleaner-applications/${appId}/bg-check/report`).catch(() => toast.error("Could not load report"))}
            data-testid="download-report-btn">
            <Download className="w-3 h-3 inline mr-1" /> Download PDF
          </button>
        )}
      </div>
      {bg.result_notes && <p className="text-xs text-[#4B5563] italic">"{bg.result_notes}"</p>}

      {!isOwner ? (
        <p className="text-xs text-[#9CA3AF]" data-testid="result-actions-owner-note">Only an account owner can activate or start the adverse-action process.</p>
      ) : bg.result === "clear" ? (
        <Button onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/activate`), "Cleaner activated")}
          disabled={busy} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="activate-as-cleaner-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Activate as cleaner
        </Button>
      ) : bg.result === "consider" ? (
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/activate`), "Cleaner activated")}
            disabled={busy} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="activate-anyway-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Activate as cleaner anyway
          </Button>
          <Button onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/pre-adverse`), "Pre-adverse action email sent")}
            disabled={busy} variant="outline" className="rounded-xl text-rose-600 border-rose-300 hover:bg-rose-50 font-semibold" data-testid="reject-start-adverse-btn">
            <XCircle className="w-4 h-4 mr-2" /> Reject and start adverse action
          </Button>
        </div>
      ) : (
        <Button onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/pre-adverse`), "Pre-adverse action email sent")}
          disabled={busy} className="rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold" data-testid="send-pre-adverse-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />} Send pre-adverse action email
        </Button>
      )}
      <Badge className={`border font-medium ${badge.cls}`}>{badge.label}</Badge>
    </div>
  );
}

function DisputeWindow({ bg, appId, isOwner, busy, run }) {
  const aa = bg.adverse_action || {};
  const ends = aa.dispute_window_ends_at ? new Date(aa.dispute_window_ends_at) : null;
  const daysLeft = ends ? Math.max(0, Math.ceil((ends.getTime() - Date.now()) / 86400000)) : 0;
  const canFinal = aa.dispute_resolution === "proceed" || daysLeft <= 0;

  if (!isOwner) return <p className="text-xs text-[#9CA3AF]">Only an account owner can manage the adverse-action process.</p>;

  return (
    <div className="space-y-3" data-testid="bg-check-dispute-window">
      <div className="flex items-center gap-2 text-sm text-rose-700 font-medium">
        <Clock className="w-4 h-4" /> Pre-adverse action sent — {daysLeft > 0 ? `${daysLeft} business day(s) left to dispute` : "dispute window ended"}
      </div>
      {!aa.dispute_resolution ? (
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-lg text-xs"
            onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/dispute-resolve`, { resolution: "clear" }), "Marked resolved — cleared")}
            disabled={busy} data-testid="dispute-resolve-clear-btn">
            Mark dispute resolved — clear
          </Button>
          <Button size="sm" variant="outline" className="rounded-lg text-xs"
            onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/dispute-resolve`, { resolution: "proceed" }), "Marked resolved — proceed")}
            disabled={busy} data-testid="dispute-resolve-proceed-btn">
            Mark dispute resolved — proceed
          </Button>
        </div>
      ) : (
        <div className="text-xs text-[#4B5563]">Dispute marked as: <b>{aa.dispute_resolution}</b></div>
      )}
      <Button onClick={() => run(() => api.post(`/admin/cleaner-applications/${appId}/bg-check/final-adverse`), "Final adverse action email sent")}
        disabled={busy || !canFinal} className="rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold" data-testid="send-final-adverse-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />} Send final adverse action email
      </Button>
    </div>
  );
}
